/**
 * 审计日志管理页面
 * 支持表格视图/时间线视图、高级搜索、统计图表、导出、详情展开
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Space, Input, Form, Select, Tag, Row, Col,
  Statistic, Drawer, Descriptions, Timeline, DatePicker, Segmented,
  message, Tooltip, Pagination, Progress,
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, DownloadOutlined,
  UnorderedListOutlined, FieldTimeOutlined,
  CheckCircleOutlined, CloseCircleOutlined,
  ExclamationCircleOutlined, AppstoreOutlined,
  EyeOutlined, BarChartOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { getAuditLogList, getAuditStatistics, exportAuditLogs } from '../../api/logs';
import type {
  AuditLogInfo,
  AuditLogQueryParams,
  AuditStatisticsResp,
  ModuleStat,
  ActionStat,
  UserOperateStat,
} from '../../api/types';
import { AuditAction, AuditResult } from '../../api/types';

const { RangePicker } = DatePicker;

/** 操作类型标签颜色映射 */
const actionColorMap: Record<string, string> = {
  [AuditAction.CREATE]: 'green',
  [AuditAction.UPDATE]: 'blue',
  [AuditAction.DELETE]: 'red',
  [AuditAction.QUERY]: 'default',
  [AuditAction.EXPORT]: 'purple',
  [AuditAction.IMPORT]: 'purple',
  [AuditAction.LOGIN]: 'cyan',
  [AuditAction.LOGOUT]: 'cyan',
  [AuditAction.ENABLE]: 'green',
  [AuditAction.DISABLE]: 'orange',
  [AuditAction.APPROVE]: 'green',
  [AuditAction.REJECT]: 'red',
  [AuditAction.ASSIGN]: 'blue',
  [AuditAction.UNASSIGN]: 'orange',
};

/** 操作类型中文映射 */
const actionLabelMap: Record<string, string> = {
  [AuditAction.CREATE]: '创建',
  [AuditAction.UPDATE]: '更新',
  [AuditAction.DELETE]: '删除',
  [AuditAction.QUERY]: '查询',
  [AuditAction.EXPORT]: '导出',
  [AuditAction.IMPORT]: '导入',
  [AuditAction.LOGIN]: '登录',
  [AuditAction.LOGOUT]: '登出',
  [AuditAction.ENABLE]: '启用',
  [AuditAction.DISABLE]: '禁用',
  [AuditAction.APPROVE]: '审批通过',
  [AuditAction.REJECT]: '审批拒绝',
  [AuditAction.ASSIGN]: '分配',
  [AuditAction.UNASSIGN]: '取消分配',
};

/** 结果类型标签配置 */
const resultTagConfig: Record<string, { color: string; label: string }> = {
  [AuditResult.SUCCESS]: { color: 'success', label: '成功' },
  [AuditResult.FAILED]: { color: 'error', label: '失败' },
  [AuditResult.ERROR]: { color: 'warning', label: '错误' },
};

/** HTTP 方法颜色映射 */
const methodColorMap: Record<string, string> = {
  GET: 'blue',
  POST: 'green',
  PUT: 'orange',
  PATCH: 'orange',
  DELETE: 'red',
};

/* ===== Mini Bar Chart Component ===== */
const MiniBarChart: React.FC<{
  data: { label: string; value: number; color?: string }[];
  maxItems?: number;
}> = ({ data, maxItems = 5 }) => {
  const sorted = [...data].sort((a, b) => b.value - a.value).slice(0, maxItems);
  const maxValue = sorted[0]?.value || 1;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {sorted.map((item) => (
        <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            width: 70, fontSize: 12, color: '#666', textAlign: 'right',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            flexShrink: 0,
          }}>
            {item.label}
          </span>
          <div style={{ flex: 1, background: '#f5f5f5', borderRadius: 2, height: 16, overflow: 'hidden' }}>
            <div style={{
              width: `${(item.value / maxValue) * 100}%`,
              height: '100%',
              background: item.color || '#1677ff',
              borderRadius: 2,
              minWidth: 2,
              transition: 'width 0.3s',
            }} />
          </div>
          <span style={{ width: 36, fontSize: 12, color: '#333', textAlign: 'right', flexShrink: 0 }}>
            {item.value}
          </span>
        </div>
      ))}
    </div>
  );
};

type ViewMode = 'table' | 'timeline';

const AuditLogList: React.FC = () => {
  const [list, setList] = useState<AuditLogInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [statistics, setStatistics] = useState<AuditStatisticsResp | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<AuditLogInfo | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [showCharts, setShowCharts] = useState(false);
  const [searchForm] = Form.useForm();

  /** 构建查询参数 */
  const buildQueryParams = useCallback((): AuditLogQueryParams => {
    const formValues = searchForm.getFieldsValue();
    const params: AuditLogQueryParams = {
      page: pagination.current,
      page_size: pagination.pageSize,
    };

    if (formValues.module) {
      params.module = formValues.module;
    }
    if (formValues.action) {
      params.action = formValues.action;
    }
    if (formValues.result) {
      params.result = formValues.result;
    }
    if (formValues.keyword) {
      params.keyword = formValues.keyword;
    }
    if (formValues.timeRange && formValues.timeRange.length === 2) {
      params.start_time = formValues.timeRange[0].startOf('day').toISOString();
      params.end_time = formValues.timeRange[1].endOf('day').toISOString();
    }

    return params;
  }, [searchForm, pagination]);

  /** 获取审计日志列表 */
  const fetchAuditLogList = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildQueryParams();
      const res: any = await getAuditLogList(params);
      if (res?.data) {
        const data = res.data?.data || res.data;
        setList(data.list || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('获取审计日志列表失败', error);
    } finally {
      setLoading(false);
    }
  }, [buildQueryParams]);

  /** 获取统计数据 */
  const fetchStatistics = useCallback(async () => {
    setStatsLoading(true);
    try {
      const formValues = searchForm.getFieldsValue();
      const statsParams: { start_time?: string; end_time?: string } = {};
      if (formValues.timeRange && formValues.timeRange.length === 2) {
        statsParams.start_time = formValues.timeRange[0].startOf('day').toISOString();
        statsParams.end_time = formValues.timeRange[1].endOf('day').toISOString();
      }
      const res: any = await getAuditStatistics(statsParams);
      if (res?.data) {
        setStatistics(res.data?.data || res.data);
      }
    } catch (error) {
      console.error('获取统计数据失败', error);
    } finally {
      setStatsLoading(false);
    }
  }, [searchForm]);

  // 初始化加载
  useEffect(() => {
    fetchAuditLogList();
    fetchStatistics();
  }, [pagination.current, pagination.pageSize]);

  /** 搜索 */
  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchAuditLogList();
    fetchStatistics();
  };

  /** 重置搜索 */
  const handleReset = () => {
    searchForm.resetFields();
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchAuditLogList();
    fetchStatistics();
  };

  /** 表格分页变更 */
  const handleTableChange = (paginationInfo: any) => {
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    });
  };

  /** 打开详情抽屉 */
  const handleViewDetail = (record: AuditLogInfo) => {
    setCurrentRecord(record);
    setDrawerVisible(true);
  };

  /** 导出审计日志 */
  const handleExport = async () => {
    setExportLoading(true);
    try {
      const params = buildQueryParams();
      const response = await exportAuditLogs(params);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `审计日志_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      message.success('导出成功');
    } catch (error) {
      console.error('导出失败', error);
      message.error('导出失败');
    } finally {
      setExportLoading(false);
    }
  };

  /** 成功率 */
  const successRate = statistics?.total_operations
    ? Math.round((statistics.success_operations / statistics.total_operations) * 100)
    : 0;

  /** 表格列定义 */
  const columns: ColumnsType<AuditLogInfo> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 70,
      fixed: 'left',
    },
    {
      title: '操作人',
      dataIndex: 'username',
      width: 100,
      ellipsis: true,
    },
    {
      title: '模块',
      dataIndex: 'module',
      width: 100,
      ellipsis: true,
    },
    {
      title: '操作类型',
      dataIndex: 'action',
      width: 100,
      render: (action: AuditAction) => (
        <Tag color={actionColorMap[action] || 'default'}>
          {actionLabelMap[action] || action}
        </Tag>
      ),
    },
    {
      title: '资源类型',
      dataIndex: 'resource_type',
      width: 110,
      ellipsis: true,
    },
    {
      title: '描述',
      dataIndex: 'description',
      width: 180,
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}>
          {text || '-'}
        </Tooltip>
      ),
    },
    {
      title: '请求',
      key: 'request',
      width: 200,
      ellipsis: true,
      render: (_, record) => (
        <Space size={4}>
          <Tag color={methodColorMap[record.method?.toUpperCase()] || 'default'}>
            {record.method?.toUpperCase()}
          </Tag>
          <Tooltip title={record.path}>
            <span style={{ fontSize: 12, color: '#666' }}>{record.path}</span>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: '结果',
      dataIndex: 'result',
      width: 80,
      render: (result: AuditResult) => {
        const config = resultTagConfig[result] || { color: 'default', label: result };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: '耗时',
      dataIndex: 'duration',
      width: 90,
      sorter: (a, b) => a.duration - b.duration,
      render: (duration: number) => {
        const color = duration > 1000 ? '#ff4d4f' : duration > 500 ? '#faad14' : '#52c41a';
        return <span style={{ color }}>{duration}ms</span>;
      },
    },
    {
      title: '客户端IP',
      dataIndex: 'client_ip',
      width: 130,
      render: (ip: string) => ip || '-',
    },
    {
      title: '操作时间',
      dataIndex: 'created_at',
      width: 170,
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
      render: (text: string) => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: '操作',
      key: 'action_col',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record)}
        >
          详情
        </Button>
      ),
    },
  ];

  /** 展开行渲染 */
  const expandedRowRender = (record: AuditLogInfo) => (
    <Descriptions
      size="small"
      column={2}
      bordered
      labelStyle={{ width: 100, fontWeight: 500, fontSize: 12 }}
      contentStyle={{ fontSize: 12 }}
    >
      <Descriptions.Item label="日志ID">{record.id}</Descriptions.Item>
      <Descriptions.Item label="租户ID">{record.tenant_id}</Descriptions.Item>
      <Descriptions.Item label="用户ID">{record.user_id}</Descriptions.Item>
      <Descriptions.Item label="资源ID">{record.resource_id || '-'}</Descriptions.Item>
      <Descriptions.Item label="请求路径" span={2}>
        <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>
          {record.method?.toUpperCase()} {record.path}
        </code>
      </Descriptions.Item>
      <Descriptions.Item label="状态码">
        <Tag color={record.status_code >= 200 && record.status_code < 300 ? 'success' : 'error'}>
          {record.status_code}
        </Tag>
      </Descriptions.Item>
      <Descriptions.Item label="耗时">
        {(() => {
          const color = record.duration > 1000 ? '#ff4d4f'
            : record.duration > 500 ? '#faad14' : '#52c41a';
          return <span style={{ color, fontWeight: 500 }}>{record.duration}ms</span>;
        })()}
      </Descriptions.Item>
      <Descriptions.Item label="描述" span={2}>
        {record.description || '-'}
      </Descriptions.Item>
      <Descriptions.Item label="客户端IP">{record.client_ip || '-'}</Descriptions.Item>
      <Descriptions.Item label="操作时间">
        {record.created_at ? dayjs(record.created_at).format('YYYY-MM-DD HH:mm:ss') : '-'}
      </Descriptions.Item>
    </Descriptions>
  );

  /** 时间线视图渲染 */
  const renderTimeline = () => {
    if (!list.length) {
      return (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
          暂无数据
        </div>
      );
    }

    return (
      <Timeline
        style={{ padding: '16px 0' }}
        items={list.map((item) => {
          const resultConfig = resultTagConfig[item.result] || { color: 'default', label: item.result };
          const dotColor =
            item.result === AuditResult.SUCCESS ? 'green' :
            item.result === AuditResult.FAILED ? 'red' : 'orange';

          return {
            key: item.id,
            color: dotColor,
            children: (
              <div style={{ paddingBottom: 8 }}>
                <div style={{ marginBottom: 4 }}>
                  <span style={{ color: '#999', fontSize: 12 }}>
                    {dayjs(item.created_at).format('YYYY-MM-DD HH:mm:ss')}
                  </span>
                </div>
                <div style={{ marginBottom: 4 }}>
                  <strong>{item.username}</strong>
                  {' '}
                  <Tag color={actionColorMap[item.action] || 'default'} style={{ marginRight: 4 }}>
                    {actionLabelMap[item.action] || item.action}
                  </Tag>
                  {' '}
                  <span>{item.resource_type}</span>
                  {item.resource_id && (
                    <span style={{ color: '#999' }}> (#{item.resource_id})</span>
                  )}
                  {' '}
                  <Tag color={resultConfig.color}>{resultConfig.label}</Tag>
                </div>
                {item.description && (
                  <div style={{ color: '#666', fontSize: 13 }}>
                    {item.description}
                  </div>
                )}
                <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
                  <Tag color={methodColorMap[item.method?.toUpperCase()] || 'default'} style={{ fontSize: 11 }}>
                    {item.method?.toUpperCase()}
                  </Tag>
                  {item.path}
                  {' | '}
                  {item.duration}ms
                  {item.client_ip && ` | ${item.client_ip}`}
                </div>
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, marginTop: 2 }}
                  onClick={() => handleViewDetail(item)}
                >
                  查看详情
                </Button>
              </div>
            ),
          };
        })}
      />
    );
  };

  return (
    <div className="audit-log-list-container">
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={5}>
          <Card loading={statsLoading} size="small">
            <Statistic
              title="总操作数"
              value={statistics?.total_operations ?? 0}
              prefix={<AppstoreOutlined />}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card loading={statsLoading} size="small">
            <Statistic
              title="成功操作"
              value={statistics?.success_operations ?? 0}
              valueStyle={{ color: '#3f8600' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card loading={statsLoading} size="small">
            <Statistic
              title="失败操作"
              value={statistics?.failed_operations ?? 0}
              valueStyle={{ color: '#cf1322' }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card loading={statsLoading} size="small">
            <Statistic
              title="成功率"
              value={successRate}
              suffix="%"
              valueStyle={{ color: successRate >= 90 ? '#3f8600' : successRate >= 70 ? '#faad14' : '#cf1322' }}
            />
            <Progress
              percent={successRate}
              size="small"
              showInfo={false}
              strokeColor={successRate >= 90 ? '#52c41a' : successRate >= 70 ? '#faad14' : '#ff4d4f'}
              style={{ marginTop: 4 }}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card loading={statsLoading} size="small">
            <Statistic
              title="活跃用户数"
              value={statistics?.user_stats?.length ?? 0}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* 统计图表 */}
      {showCharts && statistics && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Card title="模块分布" size="small" loading={statsLoading}>
              {statistics.module_stats?.length ? (
                <MiniBarChart
                  data={statistics.module_stats.map((s: ModuleStat) => ({
                    label: s.module,
                    value: s.count,
                    color: '#1677ff',
                  }))}
                />
              ) : (
                <div style={{ textAlign: 'center', color: '#999', padding: 16 }}>暂无数据</div>
              )}
            </Card>
          </Col>
          <Col span={8}>
            <Card title="操作类型分布" size="small" loading={statsLoading}>
              {statistics.action_stats?.length ? (
                <MiniBarChart
                  data={statistics.action_stats.map((s: ActionStat) => ({
                    label: actionLabelMap[s.action] || s.action,
                    value: s.count,
                    color: actionColorMap[s.action] === 'red' ? '#ff4d4f' :
                           actionColorMap[s.action] === 'green' ? '#52c41a' :
                           actionColorMap[s.action] === 'blue' ? '#1677ff' :
                           actionColorMap[s.action] === 'purple' ? '#722ed1' : '#666',
                  }))}
                />
              ) : (
                <div style={{ textAlign: 'center', color: '#999', padding: 16 }}>暂无数据</div>
              )}
            </Card>
          </Col>
          <Col span={8}>
            <Card title="用户操作排行" size="small" loading={statsLoading}>
              {statistics.user_stats?.length ? (
                <MiniBarChart
                  data={statistics.user_stats.map((s: UserOperateStat) => ({
                    label: s.username,
                    value: s.count,
                    color: '#13c2c2',
                  }))}
                />
              ) : (
                <div style={{ textAlign: 'center', color: '#999', padding: 16 }}>暂无数据</div>
              )}
            </Card>
          </Col>
        </Row>
      )}

      <Card title="审计日志" bordered={false}>
        {/* 高级搜索表单 */}
        <Form
          form={searchForm}
          layout="inline"
          onFinish={handleSearch}
          style={{ marginBottom: 16, flexWrap: 'wrap', gap: '8px 0' }}
        >
          <Form.Item name="module">
            <Input
              placeholder="模块"
              allowClear
              style={{ width: 140 }}
            />
          </Form.Item>
          <Form.Item name="action">
            <Select
              placeholder="操作类型"
              style={{ width: 130 }}
              allowClear
              options={Object.entries(AuditAction).map(([, value]) => ({
                value,
                label: actionLabelMap[value] || value,
              }))}
            />
          </Form.Item>
          <Form.Item name="result">
            <Select
              placeholder="结果"
              style={{ width: 110 }}
              allowClear
              options={[
                { value: AuditResult.SUCCESS, label: '成功' },
                { value: AuditResult.FAILED, label: '失败' },
                { value: AuditResult.ERROR, label: '错误' },
              ]}
            />
          </Form.Item>
          <Form.Item name="timeRange">
            <RangePicker
              style={{ width: 260 }}
              placeholder={['开始时间', '结束时间']}
            />
          </Form.Item>
          <Form.Item name="keyword">
            <Input
              placeholder="关键词搜索"
              allowClear
              style={{ width: 160 }}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                搜索
              </Button>
              <Button onClick={handleReset} icon={<ReloadOutlined />}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>

        {/* 工具栏 */}
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Segmented
              value={viewMode}
              onChange={(val) => setViewMode(val as ViewMode)}
              options={[
                { label: '表格视图', value: 'table', icon: <UnorderedListOutlined /> },
                { label: '时间线视图', value: 'timeline', icon: <FieldTimeOutlined /> },
              ]}
            />
            <Button
              icon={<BarChartOutlined />}
              type={showCharts ? 'primary' : 'default'}
              ghost={showCharts}
              onClick={() => setShowCharts(!showCharts)}
            >
              {showCharts ? '收起图表' : '统计图表'}
            </Button>
          </Space>
          <Button
            icon={<DownloadOutlined />}
            loading={exportLoading}
            onClick={handleExport}
          >
            导出
          </Button>
        </div>

        {/* 表格视图 */}
        {viewMode === 'table' && (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={list}
            pagination={{
              ...pagination,
              total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (t) => `共 ${t} 条`,
            }}
            loading={loading}
            onChange={handleTableChange}
            scroll={{ x: 1500 }}
            locale={{ emptyText: '暂无数据' }}
            expandable={{
              expandedRowRender,
              expandRowByClick: false,
            }}
          />
        )}

        {/* 时间线视图 */}
        {viewMode === 'timeline' && (
          <>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                加载中...
              </div>
            ) : (
              renderTimeline()
            )}
            {/* 时间线分页 */}
            {total > 0 && (
              <div style={{ textAlign: 'right', marginTop: 16 }}>
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={total}
                  showSizeChanger
                  showQuickJumper
                  showTotal={(t) => `共 ${t} 条`}
                  onChange={(page, pageSize) => {
                    setPagination({ current: page, pageSize });
                  }}
                />
              </div>
            )}
          </>
        )}
      </Card>

      {/* 详情抽屉 */}
      <Drawer
        title="审计日志详情"
        placement="right"
        width={640}
        open={drawerVisible}
        onClose={() => {
          setDrawerVisible(false);
          setCurrentRecord(null);
        }}
      >
        {currentRecord && (
          <Descriptions
            bordered
            column={2}
            size="small"
            labelStyle={{ fontWeight: 500, width: 120 }}
          >
            <Descriptions.Item label="日志ID">{currentRecord.id}</Descriptions.Item>
            <Descriptions.Item label="租户ID">{currentRecord.tenant_id}</Descriptions.Item>
            <Descriptions.Item label="用户ID">{currentRecord.user_id}</Descriptions.Item>
            <Descriptions.Item label="操作人">{currentRecord.username}</Descriptions.Item>
            <Descriptions.Item label="模块">{currentRecord.module}</Descriptions.Item>
            <Descriptions.Item label="资源类型">{currentRecord.resource_type}</Descriptions.Item>
            <Descriptions.Item label="资源ID">{currentRecord.resource_id || '-'}</Descriptions.Item>
            <Descriptions.Item label="操作类型">
              <Tag color={actionColorMap[currentRecord.action] || 'default'}>
                {actionLabelMap[currentRecord.action] || currentRecord.action}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="描述" span={2}>
              {currentRecord.description || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="请求方法">
              <Tag color={methodColorMap[currentRecord.method?.toUpperCase()] || 'default'}>
                {currentRecord.method?.toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="状态码">
              <Tag color={currentRecord.status_code >= 200 && currentRecord.status_code < 300 ? 'success' : 'error'}>
                {currentRecord.status_code}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="请求路径" span={2}>
              <code style={{ background: '#f5f5f5', padding: '2px 8px', borderRadius: 4 }}>
                {currentRecord.path}
              </code>
            </Descriptions.Item>
            <Descriptions.Item label="执行结果">
              {(() => {
                const config = resultTagConfig[currentRecord.result] || { color: 'default', label: currentRecord.result };
                return <Tag color={config.color}>{config.label}</Tag>;
              })()}
            </Descriptions.Item>
            <Descriptions.Item label="耗时">
              {(() => {
                const color = currentRecord.duration > 1000 ? '#ff4d4f'
                  : currentRecord.duration > 500 ? '#faad14' : '#52c41a';
                return <span style={{ color, fontWeight: 500 }}>{currentRecord.duration}ms</span>;
              })()}
            </Descriptions.Item>
            <Descriptions.Item label="客户端IP">
              {currentRecord.client_ip || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="操作时间">
              {currentRecord.created_at
                ? dayjs(currentRecord.created_at).format('YYYY-MM-DD HH:mm:ss')
                : '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </div>
  );
};

export default AuditLogList;
